// Account webhooks (086): the endpoints an account's Owner registers under
// Settings › API, and each endpoint's delivery log.
import { apiFetch } from './client';

export type WebhookScope = { kind: 'all' } | { kind: 'forms'; formIds: number[] };

export interface AccountWebhook {
  id: number;
  name: string;
  url: string;
  events: string[];
  scope: WebhookScope;
  enabled: boolean;
  failure_streak: number;
  /** set when the endpoint switched ITSELF off, with the last answer it gave */
  disabled_at: string | null;
  disabled_reason: string | null;
  created_at: string;
  last_delivery_at: string | null;
}

export interface WebhookEventSpec {
  type: string;
  group: string;
  label: string;
  description: string;
  formScoped: boolean;
  sample: Record<string, unknown>;
}

export interface WebhookAttempt {
  id: number;
  status: 'pending' | 'success' | 'failed' | 'dead';
  attempts: number;
  last_error: string | null;
  response: { status?: number; body?: string } | null;
  next_attempt_at: string;
  updated_at: string;
  type: string;
  event_id: string;
  payload: Record<string, unknown>;
}

export interface WebhookInput {
  name: string;
  url: string;
  events: string[];
  scope: WebhookScope;
}

export const listWebhooksApi = () =>
  apiFetch<{
    success: boolean; webhooksEnabled?: boolean; webhooks?: AccountWebhook[]; catalog?: WebhookEventSpec[];
    max?: number; keepDays?: number;
    /** the delivery job's own numbers, for the reference (B6); absent from older servers */
    contract?: Partial<import('../pages/admin/webhookDocs').WebhookContract>;
  }>('/admin/webhooks');
export const createWebhookApi = (w: WebhookInput) =>
  apiFetch<{ success: boolean; webhook: AccountWebhook; secret: string }>('/admin/webhooks', { method: 'POST', body: JSON.stringify(w) });
export const updateWebhookApi = (id: number, patch: Partial<WebhookInput> & { enabled?: boolean }) =>
  apiFetch<{ success: boolean; webhook: AccountWebhook }>(`/admin/webhooks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const rotateWebhookSecretApi = (id: number) =>
  apiFetch<{ success: boolean; secret: string }>(`/admin/webhooks/${id}/rotate-secret`, { method: 'POST' });
export const deleteWebhookApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/webhooks/${id}`, { method: 'DELETE' });
export const listWebhookAttemptsApi = (id: number, before?: number) =>
  apiFetch<{ success: boolean; attempts: WebhookAttempt[]; nextBefore?: number }>(
    `/admin/webhooks/${id}/attempts${before ? `?before=${before}` : ''}`);
export const testWebhookApi = (id: number) =>
  apiFetch<{ success: boolean; attempt: WebhookAttempt | null }>(`/admin/webhooks/${id}/test`, { method: 'POST' });
export const replayWebhookAttemptApi = (attemptId: number) =>
  apiFetch<{ success: boolean }>(`/admin/webhooks/attempts/${attemptId}/replay`, { method: 'POST' });
export const replayFailedWebhookApi = (id: number) =>
  apiFetch<{ success: boolean; queued: number }>(`/admin/webhooks/${id}/replay-failed`, { method: 'POST' });
